import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ZodError } from 'zod';

import handleApiError, { isApiError } from '@@api/errorHandler';
import { HTTP_STATUS_CODES } from '@@constants/http';
import { useAuthStore } from '@@scripts/store/useAuthStore';

import { ClientValidationError } from './lib/ClientValidationError';

export const etagCache = new Map();

const onError = (error: unknown) => {
    if (isApiError(error) && error.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
        useAuthStore.getState().clearAuth();
    }

    handleApiError(error);
};
const queryCache = new QueryCache({
    onError: (error, query) => {
        // For resources where a 404 is an expected state, not an error
        if (
            query.meta?.ignoreNotFoundErrors &&
            isApiError(error) &&
            error.status === HTTP_STATUS_CODES.NOT_FOUND
        ) {
            return;
        }

        onError(error);
    },
});

const mutationCache = new MutationCache({
    onError,
});

const queryClient = new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
        queries: {
            // A 1 second cache helps with reducing duplicate requests on some scenarios,
            // while keeping the data fresh enough for most cases.
            staleTime: 1000,
            retry: (failureCount, error: unknown) => {
                // We want Zod errors to fail immediately
                // because no amount of retries will fix them.
                if (
                    error &&
                    (error instanceof ZodError || error instanceof ClientValidationError)
                ) {
                    return false;
                }

                return failureCount < 3;
            },
        },
    },
});

export default queryClient;
